import React, { useMemo } from 'react';
import { Box, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SelectField from '../inputs/SelectField';
import { SPECIAL_REQUEST_CATEGORIES } from '../../constants/appConstants';
import { buildShapeSelectOptions, SHAPE_OTHER } from '../../utils/specialRequestDisplay';

export default function SpecialRequestFormFields({
  form,
  onChange,
  filterOptions = {},
}) {
  const { t } = useTranslation();
  const category = form.category;
  const details = form.details || {};
  const certified = category === SPECIAL_REQUEST_CATEGORIES.CERTIFIED;
  const lot = category === SPECIAL_REQUEST_CATEGORIES.NON_CERTIFIED;
  const other = category === SPECIAL_REQUEST_CATEGORIES.OTHER;

  const shapes = useMemo(() => {
    if (lot) return filterOptions.lotShapes || [];
    if (certified) return filterOptions.certifiedShapes || [];
    return filterOptions.shapes || [];
  }, [lot, certified, filterOptions.lotShapes, filterOptions.certifiedShapes, filterOptions.shapes]);

  const shapeOptions = useMemo(
    () => buildShapeSelectOptions(shapes, t),
    [shapes, t],
  );

  const colorOptions = useMemo(
    () => (filterOptions.colors || []).map((c) => ({ value: c, label: c })),
    [filterOptions.colors],
  );

  const clarityOptions = useMemo(
    () => (filterOptions.clarities || []).map((c) => ({ value: c, label: c })),
    [filterOptions.clarities],
  );

  const setDetails = (patch) => {
    onChange({ ...form, details: { ...details, ...patch } });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {(certified || lot) && (
        <>
          <SelectField
            label={t('inventory.col.shape')}
            value={details.shape}
            onChange={(value) => setDetails({ shape: value, shapeOther: value === SHAPE_OTHER ? details.shapeOther : '' })}
            options={shapeOptions}
            size="small"
            required
          />
          {details.shape === SHAPE_OTHER && (
            <TextField
              fullWidth
              size="small"
              required
              label={t('specialRequests.shapeOtherLabel')}
              value={details.shapeOther}
              onChange={(e) => setDetails({ shapeOther: e.target.value })}
            />
          )}
        </>
      )}

      {certified && (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <SelectField
              label={t('inventory.col.color')}
              value={details.color}
              onChange={(value) => setDetails({ color: value })}
              options={colorOptions}
              size="small"
              sx={{ flex: 1, minWidth: 120 }}
              emptyLabel="—"
              includeEmptyOption
            />
            <SelectField
              label={t('inventory.col.clarity')}
              value={details.clarity}
              onChange={(value) => setDetails({ clarity: value })}
              options={clarityOptions}
              size="small"
              sx={{ flex: 1, minWidth: 120 }}
              emptyLabel="—"
              includeEmptyOption
            />
            <TextField
              size="small"
              required
              type="number"
              label={t('inventory.col.carat')}
              value={details.carat}
              onChange={(e) => setDetails({ carat: e.target.value })}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ flex: 1, minWidth: 120 }}
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            label={t('specialRequests.comment')}
            value={details.comment}
            onChange={(e) => setDetails({ comment: e.target.value })}
            helperText={t('specialRequests.commentHelp')}
          />
        </>
      )}

      {lot && (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              type="number"
              label={t('specialRequests.lengthMm')}
              value={details.length}
              onChange={(e) => setDetails({ length: e.target.value })}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ flex: 1, minWidth: 120 }}
            />
            <TextField
              size="small"
              type="number"
              label={t('specialRequests.widthMm')}
              value={details.width}
              onChange={(e) => setDetails({ width: e.target.value })}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ flex: 1, minWidth: 120 }}
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            label={t('specialRequests.comment')}
            value={details.comment}
            onChange={(e) => setDetails({ comment: e.target.value })}
            helperText={t('specialRequests.commentHelp')}
          />
        </>
      )}

      {other && (
        <TextField
          fullWidth
          multiline
          rows={5}
          size="small"
          required
          label={t('specialRequests.description')}
          value={form.description}
          onChange={(e) => onChange({ ...form, description: e.target.value })}
          helperText={t('specialRequests.descriptionHelp')}
        />
      )}
    </Box>
  );
}
